// Mirrors call-engine state into the Android-native UX plugins and syncs push
// registration through the existing PATCH /sessions/current flow. Pure
// subscription glue: the engine (J8) stays platform-free, this module watches
// its published store.
//
// It runs inside the headless `brain.html` page (docs/09 §2) — there is no
// React tree in the Android WebView any more — so the two things it used to
// read out of the app's localStorage stores (the friends cache and the alias
// map) now arrive as an injected `ContactsDirectory`, fed by the brain's
// `contacts` channel. Nothing else about the wiring changed.

import type { UpdateSessionRequest } from '@merkury/protocol';
import type { ReadableStore } from '@merkury/brain/call/store.js';
import type { ActiveCallState, CallEngineState, EndedCallState } from '@merkury/brain/call/types.js';
import type { KvStore } from '@merkury/brain/storage/kv.js';
import { setNativeMicPermission } from '@merkury/brain/call/media/micPermission.js';
import { parseDialFragment } from '../../sw/messages.js';
import type { MerkuryCallServicePlugin, MerkuryPlugins, PluginListenerHandle } from './plugins.js';

/**
 * The friends the native surfaces mirror, with EFFECTIVE (alias-aware) names
 * already applied (docs/08 §4): the alias wins over the friend's real name and
 * NEVER leaves the device. Backed by the brain's `contacts` channel envelope.
 */
export interface DirectoryFriend {
  userId: string;
  /** alias ?? real display name — what the ring, the log and the watch show. */
  name: string;
  avatarSeed?: number | undefined;
}

export interface ContactsDirectory {
  /** Effective name for a peer, or undefined on a miss (native then falls
   *  back to its own generic copy). */
  effectiveName(userId: string): string | undefined;
  /** Everyone we could be called by or dial, newest snapshot. */
  friends(): readonly DirectoryFriend[];
  /** Fires on every list OR alias change; returns an unsubscribe. */
  subscribe(listener: () => void): () => void;
}

/** The slice of CallEngine the native integrations need. */
export interface CapacitorEngineLike {
  readonly state: ReadableStore<CallEngineState>;
  decline(): void;
  /** Hang up the live call (ongoing-notification hang-up button). */
  hangup(): void;
  /** Set mute for the live call (watch MUTE relay). */
  setMuted(muted: boolean): void;
  /** Start an outgoing call to a friend (watch DIAL relay); idempotent/guarded
   *  in the engine, and additionally gated here to idle + known-friend. */
  dial(peerUserId: string): void;
  /** Immediate endpoint re-race (skip reconnect backoff). */
  retrySignaling(): void;
  /** OS-level network change → ICE restart path for a live call. */
  noteNetworkChange(): void;
  /** Native audio-route feedback (current device + Bluetooth availability). */
  noteAudioRoute(state: {
    route: 'EARPIECE' | 'SPEAKER' | 'BLUETOOTH' | 'WIRED_HEADSET';
    bluetoothAvailable: boolean;
  }): void;
  /** call.end for a call the engine never surfaced; returns an unsubscribe. */
  onStrayEnd(handler: (callId: string) => void): () => void;
}

/** The slice of `window` the watch `#dial=<peerId>` handoff needs (injectable). */
export interface DialWindowLike {
  location: { hash: string; pathname: string; search: string };
  history: { replaceState(data: unknown, unused: string, url?: string): void };
  addEventListener(type: 'hashchange', listener: () => void): void;
  removeEventListener(type: 'hashchange', listener: () => void): void;
}

export interface CapacitorIntegrationDeps {
  engine: CapacitorEngineLike;
  /** `RestClient.updateCurrentSession` — protocol PushSubscription shape. */
  updateSession: (body: UpdateSessionRequest) => Promise<void>;
  /** Persisted user choices (push transport, stay-reachable). */
  kv: KvStore;
  plugins: MerkuryPlugins;
  /** Effective friend names for the ring / ongoing notification / watch. */
  contacts: ContactsDirectory;
  /** Window carrying the native `#dial=` handoff; defaults to the real one. */
  win?: DialWindowLike;
}

export const PUSH_TRANSPORT_KEY = 'push-transport';
export const STAY_REACHABLE_KEY = 'stay-reachable';

export type PushTransport = 'fcm' | 'ntfy';

/** A call in one of these phases keeps the `microphone` foreground service up. */
const ACTIVE_PHASES: ReadonlySet<ActiveCallState['phase']> = new Set([
  'connecting',
  'in_call',
  'reconnecting',
]);

/** wearcore CallPhase wire value the watch should show for an engine call. */
export type WearPhase = 'idle' | 'ringing' | 'inCall';

/**
 * Map an engine call to the coarse phase the watch renders. Only an INCOMING
 * ring is `ringing` (the watch's accept/decline screen); every other live phase
 * — outgoing dialing/ringing_remote, connecting, in_call, reconnecting — reads
 * as `inCall` so the watch shows hangup/mute and never a scary transitional
 * state (prime directive: don't alarm; a call being nursed is still a call).
 */
export function wearPhase(call: ActiveCallState | null): WearPhase {
  if (call === null) return 'idle';
  if (call.phase === 'ringing' && call.direction === 'incoming') return 'ringing';
  return 'inCall';
}

/**
 * Signaling statuses that mean "the JS client owns (or is claiming) the
 * session". The lease below is asserted regardless on a heartbeat — a JS that
 * merely EXISTS must keep native down (it may connect at any moment) — but a
 * transition into one of these re-asserts immediately, so the native socket
 * closes before the JS one authenticates (no supersede flap).
 */
const JS_SIGNALING_ALIVE: ReadonlySet<CallEngineState['signaling']> = new Set([
  'connecting',
  'connected',
  'reconnecting',
]);

/**
 * How often the JS side re-asserts its liveness lease to the native gate.
 * Comfortably below the native lease TTL (90 s, NativeReachGate.JS_LEASE_TTL_MS)
 * so a healthy WebView never lets the lease lapse, while a killed one stops
 * refreshing and native takes over within one TTL.
 */
export const JS_ALIVE_REASSERT_MS = 60_000;

/**
 * Settings "stay reachable" toggle (docs/06 §4): persists the choice and
 * starts/stops the persistent-WSS foreground service. Exported for the
 * Settings screen to call once J9 adds the control (reported).
 */
export async function setStayReachable(
  enabled: boolean,
  kv: KvStore,
  callService: MerkuryCallServicePlugin,
): Promise<void> {
  await kv.put(STAY_REACHABLE_KEY, enabled);
  await callService.setStayReachable({ enabled });
}

/**
 * Push registration per docs/06 §4: FCM by default, ntfy/UnifiedPush when the
 * user opted in AND a distributor is installed. UnifiedPush endpoints arrive
 * asynchronously via `pushTokenChanged` (handled by the rotation watcher).
 */
export async function syncCapacitorPush(deps: {
  push: MerkuryPlugins['push'];
  kv: KvStore;
  updateSession: (body: UpdateSessionRequest) => Promise<void>;
}): Promise<void> {
  const preferred = (await deps.kv.get<PushTransport>(PUSH_TRANSPORT_KEY)) ?? 'fcm';
  if (preferred === 'ntfy') {
    const { available } = await deps.push.isNtfyDistributorAvailable();
    if (available) {
      await deps.push.registerUnifiedPush();
      return; // NEW_ENDPOINT broadcast → pushTokenChanged → rotation watcher PATCHes.
    }
  }
  const { token } = await deps.push.getFcmToken();
  await deps.updateSession({ push: { kind: 'fcm', payload: token } });
}

/**
 * Wires everything and returns an idempotent teardown. Any individual native
 * call may fail (plugin missing, OS denial) — failures are swallowed: native
 * niceties must never break the call path (prime directive).
 */
export async function wireCapacitorIntegrations(
  deps: CapacitorIntegrationDeps,
): Promise<() => void> {
  const { engine, kv, plugins, updateSession, contacts } = deps;
  const { callNotification, callService, audioRouting, push, wearBridge } = plugins;
  const effectiveName = (userId: string): string | undefined => contacts.effectiveName(userId);

  // Inject the real native mic-permission probe: without this,
  // brain/call/media/micPermission.js stays on its pure-browser path even
  // inside Capacitor (it no longer reaches into this package on its own).
  setNativeMicPermission({
    request: async () => (await callService.requestMicPermission()).granted,
    status: async () => (await callService.getMicPermissionStatus()).granted,
  });

  const swallow = (p: Promise<unknown>): void => void p.catch(() => undefined);

  // --- JS-liveness lease → native stand-down gate ---------------------------
  // The WebView can be resurrected by the OEM in a paused activity (field-
  // verified on ColorOS after kill -9), so activity lifecycle alone cannot
  // tell native whether JS is alive. This lease can: asserted here on wire,
  // re-asserted on signaling transitions and on a heartbeat; released only on
  // dispose. A killed WebView simply stops refreshing.
  const noteJsAlive = (active: boolean): void =>
    swallow(callService.noteJsSignalingActive({ active }));
  noteJsAlive(true);
  const aliveInterval = setInterval(() => noteJsAlive(true), JS_ALIVE_REASSERT_MS);

  // --- Engine state → native call UX ---------------------------------------
  let shownRingingCallId: string | null = null;
  let inCallServiceRunning = false;
  let appliedRouteKey: string | null = null;
  let lastEndedSeen: EndedCallState | null = engine.state.get().lastEnded;
  let lastSignalingSeen: CallEngineState['signaling'] | null = null;
  let lastWearStateKey: string | null = null;

  const sync = (): void => {
    const s = engine.state.get();
    const call = s.call;

    // Signaling status transitions re-assert the JS lease immediately, so the
    // native listener closes its socket before ours authenticates.
    if (s.signaling !== lastSignalingSeen) {
      lastSignalingSeen = s.signaling;
      if (JS_SIGNALING_ALIVE.has(s.signaling)) noteJsAlive(true);
    }

    // Incoming ring → full-screen intent + ringtone; anything else clears it.
    const ringingCallId =
      call && call.phase === 'ringing' && call.direction === 'incoming' ? call.callId : null;
    if (ringingCallId !== shownRingingCallId) {
      if (ringingCallId && call) {
        // Guard the mic before the user can tap Accept: answering from the
        // full-screen ring can't surface the WebView getUserMedia prompt (over
        // keyguard / not foregrounded), so a first-time request there is
        // silently denied and the call dies "connection lost". Requesting
        // natively now (onboarding usually primed it already) means the grant
        // is settled by the time accept() runs getUserMedia. Fire-and-forget.
        swallow(callService.requestMicPermission());
        // Resolve the caller's EFFECTIVE display name from the offline friends
        // cache + device-local aliases (synchronous, no network) so the native
        // ring shows the alias/"<name> is calling" exactly like the React
        // screen. Absent a cache hit, native falls back to its generic copy.
        const peerName = effectiveName(call.peerUserId);
        swallow(
          callNotification.showIncomingCall({
            callId: ringingCallId,
            ...(peerName ? { peerName } : {}),
            peerId: call.peerUserId,
          }),
        );
      } else {
        swallow(callNotification.cancelIncomingCall());
      }
      shownRingingCallId = ringingCallId;
    }

    // Active call → `microphone`-type foreground service (wakelock/audio focus).
    // The resolved peer name feeds the ongoing-call notification (CallStyle
    // status-bar chip shows WHO the call is with, like the ring does).
    const active = call !== null && ACTIVE_PHASES.has(call.phase);
    if (active !== inCallServiceRunning) {
      if (active && call) {
        const peerName = effectiveName(call.peerUserId);
        // Pass the callId so the native in-call service can send the watch a
        // valid IN_CALL state the instant the call goes active — a reliable belt
        // for the locked-phone accept, where the WebView (behind the keyguard)
        // may not publish its own state until after unlock.
        swallow(
          callService.startInCall({
            callId: call.callId,
            ...(peerName ? { peerName } : {}),
            peerId: call.peerUserId,
          }),
        );
      } else {
        swallow(callService.stopInCall());
      }
      inCallServiceRunning = active;
    }

    // Audio route → AudioManager. Applied when the call becomes active and
    // re-applied on every phase/route change, not only on toggles: the
    // WebView's WebRTC audio manager registers its own speaker communication
    // route around dial time, and the newest setCommunicationDevice request
    // wins — ours has to keep being newest for the earpiece default (docs/06
    // §4) to hold. Un-pinning at call end is the native side's job
    // (InCallService stop → AudioRoutingPlugin.onCallEnded), so no
    // reset is sent here when the call goes away.
    const routeKey = call && active ? `${call.callId}:${call.phase}:${call.audioRequest}` : null;
    if (routeKey !== appliedRouteKey) {
      if (call && routeKey !== null) swallow(audioRouting.setRoute({ route: call.audioRequest }));
      appliedRouteKey = routeKey;
    }

    // Missed incoming ring → missed-call notification.
    if (s.lastEnded && s.lastEnded !== lastEndedSeen) {
      if (s.lastEnded.cause === 'missed') {
        swallow(callNotification.showMissedCall({ callId: s.lastEnded.callId }));
      }
      // A call that ended without another one active means any keyguard
      // exemption armed by an `#invite=` deep link has lost its justification
      // (the caller hung up during ring / the call never connected) — drop it
      // NOW so a stale Accept can't leave the app exposed over the lock
      // screen. Redundant (harmless) after a normal in-call teardown, where
      // stopInCall clears it anyway.
      if (call === null) swallow(callService.clearKeyguardOverlay());
      lastEndedSeen = s.lastEnded;
    }

    // Wear watch: push the authoritative call snapshot on any meaningful change
    // (phase/peer/timer/mute/route). Deduped by a composite key so unrelated
    // engine churn doesn't spam the Data Layer.
    // Never tell the watch "inCall/ringing" without a callId: the very first
    // dialing tick has a call object but no id yet, and a non-idle snapshot with
    // no callId is un-actionable (and crashed the watch UI). Hold at idle until
    // the id exists; the watch's local "connecting" hint covers the gap.
    const phase = call?.callId ? wearPhase(call) : 'idle';
    const wearPeerName = call ? effectiveName(call.peerUserId) : undefined;
    const startedAtMillis = call?.connectedAt ?? 0;
    const muted = call?.muted ?? false;
    const audioOnWatch = s.audio?.route === 'BLUETOOTH';
    const wearPeerId = call?.peerUserId;
    const wearKey = `${phase}:${call?.callId ?? ''}:${wearPeerName ?? ''}:${wearPeerId ?? ''}:${startedAtMillis}:${muted}:${audioOnWatch}`;
    if (wearKey !== lastWearStateKey) {
      lastWearStateKey = wearKey;
      swallow(
        wearBridge.publishState({
          phase,
          ...(call?.callId ? { callId: call.callId } : {}),
          ...(wearPeerName ? { peerName: wearPeerName } : {}),
          ...(wearPeerId ? { peerId: wearPeerId } : {}),
          startedAtMillis,
          muted,
          audioOnWatch,
        }),
      );
    }
  };

  const unsubscribe = engine.state.subscribe(sync);
  sync();

  // A call.end for a call the engine never surfaced (shadowed crossed half;
  // an invite only the push receiver rang) still has to kill any native ring
  // for it — targeted by callId so a different live ring is never clipped.
  const unsubscribeStray = engine.onStrayEnd((callId) =>
    swallow(callNotification.cancelIncomingCall({ callId })),
  );

  // --- Native events → engine ------------------------------------------------
  const handles: PluginListenerHandle[] = [];
  try {
    handles.push(
      await callNotification.addListener('callDeclined', ({ callId }) => {
        const call = engine.state.get().call;
        if (call && call.callId === callId && call.phase === 'ringing') engine.decline();
      }),
    );
    handles.push(
      await callNotification.addListener('callHangup', () => {
        // Ongoing-notification hang-up: end whatever call is live (there is at
        // most one). The native side stops the FGS regardless; this sends the
        // actual call.end so the peer doesn't wait out a timeout.
        if (engine.state.get().call !== null) engine.hangup();
      }),
    );
    handles.push(
      await push.addListener('pushTokenChanged', ({ kind, payload }) => {
        swallow(updateSession({ push: { kind, payload } }));
      }),
    );
    handles.push(
      await callService.addListener('reconnectNudge', () => {
        // Screen-on / network-return: with the CPU suspended the JS heartbeat
        // stops and the WS can die unnoticed — retry now instead of waiting
        // out the backoff, and let a live call restart ICE (docs/06 §3).
        engine.retrySignaling();
        engine.noteNetworkChange();
      }),
    );
    handles.push(
      await wearBridge.addListener('wearMute', ({ muted, callId }) => {
        // Watch toggled mute: apply to the live call only, and only when the
        // callId matches (a stale mute can't touch a different, later call).
        const call = engine.state.get().call;
        if (call && (!callId || call.callId === callId)) engine.setMuted(muted);
      }),
    );
    handles.push(
      await audioRouting.addListener('routeChanged', (data) => {
        // Native audio-route feedback → engine state (drives the in-call
        // route control: current device + whether Bluetooth is an option).
        engine.noteAudioRoute({ route: data.route, bluetoothAvailable: data.bluetoothAvailable });
      }),
    );
    // Seed the initial route state. Detection needs no Bluetooth permission —
    // availability comes from AudioManager's device list (see the native
    // plugin), so no startup prompt is raised; requestBluetoothPermission
    // stays available for a future manual affordance if some OEM needs it.
    void (async () => {
      try {
        const rs = await audioRouting.getRouteState();
        engine.noteAudioRoute({ route: rs.route, bluetoothAvailable: rs.bluetoothAvailable });
      } catch {
        // Plugin missing: the route control just hides Bluetooth.
      }
    })();
  } catch {
    // Listener registration failing means the plugin isn't there — proceed.
  }

  // --- Startup state ----------------------------------------------------------
  // Reachable by default on a fresh install (no stored choice): a censorship-
  // resistant caller should be reachable out of the box (prime directive) —
  // an explicit opt-out persists `false` and is honored. The read failing
  // (storage error) also defaults reachable rather than silently unreachable.
  const stayReachable = (await kv.get<boolean>(STAY_REACHABLE_KEY).catch(() => true)) ?? true;
  swallow(callService.setStayReachable({ enabled: stayReachable }));
  swallow(syncCapacitorPush({ push, kv, updateSession }));

  // Mirror friend EFFECTIVE names (alias ?? real — docs/08 §4) to native so the
  // reach ring can say "<name> is calling" instead of "Merkury call" (invite
  // frames carry only userIds): once from the directory now, then on every
  // change — a list refresh AND an alias edit both land on the same
  // subscription. Aliases never leave the device.
  const syncNames = (friends: readonly DirectoryFriend[]): void =>
    swallow(
      callService.syncPeerNames({ names: Object.fromEntries(friends.map((f) => [f.userId, f.name])) }),
    );
  // Wear watch dial list: the same friend snapshot — peerId + name + mercury
  // avatar seed, nothing else — as a durable DataItem (survives a watch reboot
  // / out-of-range window). The seed lets the watch draw the same mercury disc
  // the phone shows for that contact. Name is the effective (aliased) name.
  const syncWearContacts = (friends: readonly DirectoryFriend[]): void =>
    swallow(
      wearBridge.publishContacts({
        contacts: friends.map((f) => ({
          peerId: f.userId,
          name: f.name,
          ...(f.avatarSeed !== undefined ? { avatarSeed: f.avatarSeed } : {}),
        })),
      }),
    );
  const syncDirectory = (): void => {
    const friends = contacts.friends();
    // An empty directory is "not loaded yet", not "you have no friends": pushing
    // it would wipe the native name mirror a cold ring depends on.
    if (friends.length === 0) return;
    syncNames(friends);
    syncWearContacts(friends);
  };
  syncDirectory();
  const unsubscribeDirectory = contacts.subscribe(syncDirectory);

  // --- Watch DIAL → engine.dial (native hands off as a `#dial=<peerId>` hash) --
  // WearBridgeService wakes MainActivity with the hash; MainActivity sets
  // window.location.hash, which lands here (cold: the initial read; warm: the
  // hashchange). Guarded to idle + known-friend so a stray/duplicate #dial can
  // never disturb a live call or dial a stranger; engine.dial re-checks too.
  const win: DialWindowLike | undefined =
    deps.win ?? (typeof window !== 'undefined' ? (window as unknown as DialWindowLike) : undefined);
  const consumeDial = (): void => {
    if (!win) return;
    const peerId = parseDialFragment(win.location.hash);
    if (peerId === null) return;
    win.history.replaceState(null, '', win.location.pathname + win.location.search);
    if (engine.state.get().call !== null) return; // not idle
    const known = contacts.friends().some((f) => f.userId === peerId);
    if (!known) return;
    engine.dial(peerId);
  };
  if (win) {
    win.addEventListener('hashchange', consumeDial);
    consumeDial(); // cold-open: MainActivity may already have set the hash
  }

  let torndown = false;
  return () => {
    if (torndown) return;
    torndown = true;
    unsubscribe();
    unsubscribeStray();
    unsubscribeDirectory();
    if (win) win.removeEventListener('hashchange', consumeDial);
    clearInterval(aliveInterval);
    // The engine is going away with us (dispose path) — release the lease so
    // the native listener can take over without waiting out the TTL.
    noteJsAlive(false);
    setNativeMicPermission(null);
    for (const h of handles) swallow(h.remove());
    if (shownRingingCallId) swallow(callNotification.cancelIncomingCall());
    if (inCallServiceRunning) swallow(callService.stopInCall());
  };
}
